/**
 * Status strip of the planner header (#2038): drafts, published and confirmed shifts of the
 * visible range, from count-by-status (org-wide, the same scope as Publish), with the
 * publish action right there when there are drafts, and the "How it works" help.
 */
import { useLingui } from '@lingui/react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCountShiftsByStatus } from '@/hooks/useShifts';
import { PlannerHelpPopover } from './PlannerHelp';

export interface PlannerStatusStripProps {
  fromDate: string;
  toDate: string;
  onPublish: () => void;
  /**
   * True while a childcare, group or employee filter narrows the grid. The counts come
   * from count-by-status, which has no such filter, so the strip says they cover everyone.
   */
  filtered?: boolean;
}

export function PlannerStatusStrip({ fromDate, toDate, onPublish, filtered = false }: PlannerStatusStripProps) {
  const { i18n } = useLingui();
  const drafts = useCountShiftsByStatus(fromDate, toDate, 'DRAFT');
  const published = useCountShiftsByStatus(fromDate, toDate, 'PUBLISHED');
  const confirmed = useCountShiftsByStatus(fromDate, toDate, 'CONFIRMED');
  const loading = drafts.isLoading || published.isLoading || confirmed.isLoading;
  // A failed count never reads as "0 drafts" (#2038 UX pass).
  const failed = drafts.isError || published.isError || confirmed.isError;
  const draftCount = drafts.data?.count ?? 0;

  return (
    <div
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border bg-muted/40 px-3 py-2 text-sm print:hidden"
      data-testid="planner-status-strip"
    >
      <span className="text-muted-foreground">
        {i18n._({ id: 'shifts.strip.period', message: 'Period' })}:
      </span>
      {loading ? (
        <Skeleton className="h-4 w-56" />
      ) : failed ? (
        <span className="text-muted-foreground" data-testid="planner-status-strip-error">
          {i18n._({ id: 'shifts.strip.error', message: 'Counts are not available right now' })}
        </span>
      ) : (
        <span>
          {i18n._({
            id: 'shifts.strip.counts',
            message:
              '{drafts, plural, one {# draft} other {# drafts}} · {published} published · {confirmed} confirmed',
            values: {
              drafts: draftCount,
              published: published.data?.count ?? 0,
              confirmed: confirmed.data?.count ?? 0,
            },
          })}
        </span>
      )}
      {!loading && !failed && filtered && (
        <span className="text-xs text-muted-foreground" data-testid="planner-status-strip-scope">
          {i18n._({
            id: 'shifts.strip.allGroups',
            message: '(all groups and employees, the filter does not apply here)',
          })}
        </span>
      )}
      <PlannerHelpPopover />
      {!loading && !failed && draftCount > 0 && (
        <Button size="sm" className="ml-auto h-8" onClick={onPublish} data-testid="strip-publish">
          <Send className="mr-2 h-4 w-4" aria-hidden="true" />
          {i18n._({ id: 'shifts.publish.button', message: 'Publish' })}
        </Button>
      )}
    </div>
  );
}
